/**
 * Viewport context — single source of truth for mobile detection in Owner Portal.
 * Canonical breakpoint: 768px (matches owner-mobile.css and design system).
 * Use ViewportProvider at app root and useViewport() in any owner component.
 * Desktop view (viewport > 768px) is unchanged; only the source of isMobile is centralized.
 */

import { createContext, useContext, useEffect, useState } from 'react';

/** Canonical mobile breakpoint for Owner Portal (matches CSS max-width: 768px). */
export const OWNER_MOBILE_BREAKPOINT = 768;

const ViewportContext = createContext({
  isMobile: false,
  width: typeof window !== 'undefined' ? window.innerWidth : 1024,
});

function getInitialWidth() {
  if (typeof window === 'undefined') return 1024;
  return window.innerWidth;
}

export function ViewportProvider({ children }) {
  const [width, setWidth] = useState(getInitialWidth);
  const isMobile = width <= OWNER_MOBILE_BREAKPOINT;

  useEffect(() => {
    const sync = () => setWidth(window.innerWidth);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  return (
    <ViewportContext.Provider value={{ isMobile, width }}>
      {children}
    </ViewportContext.Provider>
  );
}

export function useViewport() {
  const context = useContext(ViewportContext);
  if (context === undefined) {
    throw new Error('useViewport must be used within a ViewportProvider');
  }
  return context;
}
